import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Download, Trash2, FileUp, Database, History, Save, RotateCcw } from "lucide-react";
import { filesApi, activityApi } from "@/lib/api";
import { useFinancialState } from "@/context/StateContext";
import { fmtDate } from "@/lib/finance";
import { PageHeader, SectionCard, EmptyState, AddButton } from "@/components/shared";
import { CsvImportWizard } from "@/components/CsvImportWizard";
import { RecurringBillsDialog } from "@/components/RecurringBillsDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const fmtBytes = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Settings() {
  const { state, setState, update, saveNow } = useFinancialState();
  const [files, setFiles] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(null);
  const fileRef = useRef();
  const restoreRef = useRef();

  const refresh = () => Promise.all([filesApi.list(), activityApi.list()])
    .then(([f, a]) => { setFiles(f.files || []); setActivity(a.activity || []); })
    .catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await filesApi.upload(file, "document"); toast.success("File uploaded to vault"); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDelete = async (id) => { try { await filesApi.delete(id); toast.success("File deleted"); refresh(); } catch { toast.error("Delete failed"); } };

  const download = async (f) => {
    try {
      const token = localStorage.getItem("wcc_token");
      const res = await fetch(filesApi.downloadUrl(f.id), { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = f.original_name; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const onImportConfirm = (txns) => {
    update((prev) => {
      const merged = [...txns, ...(prev.transactions || [])];
      merged.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      return { ...prev, transactions: merged };
    }, `Imported ${txns.length} transactions`);
    toast.success(`Imported ${txns.length} transactions`);
    refresh();
    // Offer to turn any repeating charges into tracked bills
    setTimeout(() => setRecurringOpen(true), 600);
  };

  const addRecurringBills = (newBills) => {
    if (!newBills.length) return;
    update((prev) => ({ ...prev, bills: [...(prev.bills || []), ...newBills] }), `Tracked ${newBills.length} recurring bill(s)`);
    toast.success(`Added ${newBills.length} recurring bill${newBills.length === 1 ? "" : "s"}`);
  };

  const backupJson = () => {
    saveNow("Backed up state");
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `wealth-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click(); URL.revokeObjectURL(url);
    toast.success("Backup downloaded");
  };

  const onRestoreFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || ""));
        if (!parsed || typeof parsed !== "object" || !("accounts" in parsed) || !("transactions" in parsed)) {
          toast.error("This doesn't look like a valid Wealth Command backup");
          return;
        }
        setPendingRestore(parsed);
      } catch {
        toast.error("Could not read that JSON file");
      } finally {
        if (restoreRef.current) restoreRef.current.value = "";
      }
    };
    reader.readAsText(file);
  };

  const confirmRestore = () => {
    if (!pendingRestore) return;
    setState(pendingRestore);
    saveNow("Restored from backup");
    setPendingRestore(null);
    toast.success("Your data has been restored");
    setTimeout(refresh, 800);
  };

  const exportCsv = () => {
    const txns = state?.transactions || [];
    if (!txns.length) return toast.error("No transactions to export");
    const header = "Date,Name,Category,Account,Type,Amount\n";
    const rows = txns.map((t) => `${t.date},"${t.name}",${t.category},${t.account},${t.type},${t.amount}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    toast.success("Transactions exported");
  };

  return (
    <div>
      <PageHeader title="Data & settings" subtitle="Your secure vault, imports, backups, and activity log." testid="settings-header" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <SectionCard title="Secure file vault" subtitle="Receipts, statements & documents (max 10MB)" testid="vault-card"
            right={<><input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="file-vault-input" />
              <AddButton onClick={() => fileRef.current?.click()} testid="file-vault-upload-button"><Upload className="h-4 w-4 mr-1.5" />Upload</AddButton></>}>
            {loading ? <Skeleton className="h-32" /> : files.length ? (
              <div className="space-y-2">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`file-row-${f.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-[var(--green)] shrink-0" />
                      <div className="min-w-0"><div className="text-sm font-medium truncate">{f.original_name}</div><div className="text-[11px] text-[var(--text-faint)]">{fmtBytes(f.size_bytes)} · {fmtDate(f.created_at)}</div></div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(f)} data-testid={`download-file-${f.id}`} aria-label="Download"><Download className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--danger)]" onClick={() => onDelete(f.id)} data-testid={`delete-file-${f.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={FileUp} title="Vault is empty" message="Upload receipts, bank statements, and documents to keep them safe." />}
          </SectionCard>

          <SectionCard title="Import transactions (CSV)" subtitle="Guided import with column mapping & preview" testid="import-card"
            right={<AddButton onClick={() => setWizardOpen(true)} testid="csv-import-open-button"><FileUp className="h-4 w-4 mr-1.5" />Import CSV</AddButton>}>
            <p className="text-sm text-[var(--text-muted)]">Upload a bank statement, map the Date / Description / Amount columns, preview the parsed transactions, then import — they're added to your Cash flow.</p>
          </SectionCard>

          <SectionCard title="Backup & restore" testid="backup-card">
            <div className="flex flex-wrap gap-3">
              <AddButton variant="secondary" onClick={backupJson} testid="backup-json-button"><Save className="h-4 w-4 mr-1.5" />Backup all data (JSON)</AddButton>
              <input ref={restoreRef} type="file" accept="application/json,.json" className="hidden" onChange={onRestoreFile} data-testid="restore-file-input" />
              <AddButton variant="secondary" onClick={() => restoreRef.current?.click()} testid="restore-button"><RotateCcw className="h-4 w-4 mr-1.5" />Restore from backup</AddButton>
              <AddButton variant="secondary" onClick={exportCsv} testid="export-csv-button"><Download className="h-4 w-4 mr-1.5" />Export transactions (CSV)</AddButton>
            </div>
            <p className="text-[11px] text-[var(--text-faint)] mt-3">Restoring replaces your entire financial picture with the backup file — useful when moving to a new device.</p>
          </SectionCard>
        </div>

        <div className="lg:col-span-5">
          <SectionCard title="Activity history" subtitle="Recent changes" testid="activity-card">
            {loading ? <Skeleton className="h-64" /> : activity.length ? (
              <div className="space-y-3 max-h-[520px] overflow-auto">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start gap-2.5" data-testid={`activity-row-${a.id}`}>
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-[color:rgba(31,115,85,0.10)] text-[var(--green)] shrink-0"><History className="h-3.5 w-3.5" /></span>
                    <div><div className="text-sm text-[var(--text)]">{a.action}</div><div className="text-[11px] text-[var(--text-faint)]">{fmtDate(a.created_at)}</div></div>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={Database} title="No activity yet" message="Your actions will appear here." />}
          </SectionCard>
        </div>
      </div>

      <CsvImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onConfirm={onImportConfirm} rules={state?.categoryRules} />

      <RecurringBillsDialog open={recurringOpen} onOpenChange={setRecurringOpen}
        transactions={state?.transactions || []} bills={state?.bills || []} onAdd={addRecurringBills} />

      <AlertDialog open={!!pendingRestore} onOpenChange={(v) => !v && setPendingRestore(null)}>
        <AlertDialogContent className="bg-[var(--surface-2)]" data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-[var(--text)]">Restore from backup?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-muted)]">
              This will replace ALL your current data (accounts, transactions, goals, and everything else) with the contents of the backup file. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[color:var(--border-subtle)]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore} className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]" data-testid="confirm-restore-button">Restore my data</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
